import cn from "classnames";
import List from "components/shared/list";
import Pagination from "components/shared/list/pagination";
import differenceInSeconds from "date-fns/differenceInSeconds";
import Tooltip from "rc-tooltip";
import React from "react";

const REFRESH_LIST_TIME = 5 * 60 * 1000; // 5 minutes

const tableColumnMapper = {
    id: "id",
    externalId: "id",
    firstLatePeriod: "status",
    "firstBidPoint/planEnterDateOffset": "from",
    "lastBidPoint/planEnterDateOffset": "to",
    "car/number": "car",
    priceWithoutCosts: "priceWithoutCosts",
};

export default class BidsSortableList extends List {
    state = {
        openedDropd: [],
        autoRefreshEnabled: false,
    };

    get tableColumnMapper() {
        return tableColumnMapper;
    }

    componentDidMount() {
        this.context?.setInitialLoad(true);
        this.getList(this.props);
        window.addEventListener("visibilitychange", this.onVisibilityChange);
    }

    componentDidUpdate(prevProps) {
        if (this.context?.initialLoad && prevProps.itemsLoadedAt !== this.props.itemsLoadedAt) {
            this.context.setInitialLoad(false);
            this.scrollContainer.scrollTop = this.context.position;
            this.prevItemTimeout = setTimeout(() => {
                this.context.setPrevItemId(undefined);
            }, 3000);
        }
    }

    componentWillUnmount() {
        clearTimeout(this.prevItemTimeout);
        this.context?.setPrevItemId?.(undefined);
        clearInterval(this.refreshTimer);
    }

    createInterval = () => {
        if (document.visibilityState === "hidden") {
            return;
        }
        this.refreshTimer = setInterval(
            () => {
                this.props.mergeParams();
            },
            REFRESH_LIST_TIME, // 5 minutes
        );
    };

    onVisibilityChange = () => {
        if (!this.state.autoRefreshEnabled) {
            return;
        }
        if (document.visibilityState === "hidden") {
            clearInterval(this.refreshTimer);
        } else {
            if (
                this.props.itemsLoadedAt &&
                differenceInSeconds(new Date(), new Date(this.props.itemsLoadedAt)) > REFRESH_LIST_TIME / 1000
            ) {
                this.props.mergeParams();
            }
            this.createInterval();
        }
    };

    scrollHandler = (e) => {
        this.context?.setPosition(e.target.scrollTop);
    };

    getList = ({ getList, params }) => {
        if (params.orderBy && !Object.keys(this.tableColumnMapper).some((item) => params.orderBy[0].includes(item))) {
            params.orderBy = ["firstBidPoint/planEnterDateOffset desc"];
        }
        getList(params);
    };

    get dropdActions() {
        return {
            id: [
                {
                    text: "ID (по возр.)",
                    action: this.props.sortData.bind(this, [{ name: "id", value: "asc" }]),
                },
                {
                    text: "ID (по убыв.)",
                    action: this.props.sortData.bind(this, [{ name: "id", value: "desc" }]),
                },
                {
                    text: "По номеру 1С (по возр.)",
                    action: this.props.sortData.bind(this, [{ name: "externalId", value: "asc" }]),
                },
                {
                    text: "По номеру 1С (по убыв.)",
                    action: this.props.sortData.bind(this, [{ name: "externalId", value: "desc" }]),
                },
            ],

            from: [
                {
                    text: "По дате загрузке (по возр.)",
                    action: this.props.sortData.bind(this, [
                        { name: "firstBidPoint/planEnterDateOffset", value: "asc" },
                    ]),
                },
                {
                    text: "По дате загрузке (по убыв.)",
                    action: this.props.sortData.bind(this, [
                        { name: "firstBidPoint/planEnterDateOffset", value: "desc" },
                    ]),
                },
            ],

            to: [
                {
                    text: "По дате выгрузки (по возр.)",
                    action: this.props.sortData.bind(this, [
                        { name: "lastBidPoint/planEnterDateOffset", value: "asc" },
                    ]),
                },
                {
                    text: "По дате выгрузки (по убыв.)",
                    action: this.props.sortData.bind(this, [
                        { name: "lastBidPoint/planEnterDateOffset", value: "desc" },
                    ]),
                },
            ],

            car: [
                {
                    text: "По машине (по возр.)",
                    action: this.props.sortData.bind(this, [
                        { name: "car/number", value: "asc" },
                        { name: "firstBidPoint/planEnterDateOffset", value: "asc" },
                    ]),
                },
                {
                    text: "По машине (по убыв.)",
                    action: this.props.sortData.bind(this, [
                        { name: "car/number", value: "desc" },
                        { name: "firstBidPoint/planEnterDateOffset", value: "asc" },
                    ]),
                },
            ],

            priceWithoutCosts: [
                {
                    text: "Цена с учётом затрат (по возр.)",
                    action: this.props.sortData.bind(this, [{ name: "priceWithoutCosts", value: "asc" }]),
                },
                {
                    text: "Цена с учётом затрат (по убыв.)",
                    action: this.props.sortData.bind(this, [{ name: "priceWithoutCosts", value: "desc" }]),
                },
            ],

            status: [
                {
                    text: "По опозданию (по возр.)",
                    action: this.props.sortData.bind(this, [{ name: "firstLatePeriod", value: "desc" }]),
                },
                {
                    text: "По опозданию (по убыв.)",
                    action: this.props.sortData.bind(this, [{ name: "firstLatePeriod", value: "asc" }]),
                },
            ],
        };
    }

    openDropd = (column) => {
        const { openedDropd } = this.state;
        if (openedDropd.length > 0) {
            return;
        }
        openedDropd.push(column);
        this.setState({ openedDropd });
        setTimeout(() => document.addEventListener("click", this.closeDropd), 0);
    };

    closeDropd = () => {
        this.setState({ openedDropd: [] });
        document.removeEventListener("click", this.closeDropd);
    };

    renderDropd = (column) => {
        const { openedDropd } = this.state;
        return (
            openedDropd.includes(column) && (
                <ul
                    className={cn("c-dropd__list c-dropd__list--wide no-bullet", {
                        "c-dropd__list--left": column !== "status",
                        "c-dropd__list--right": column === "status",
                    })}
                >
                    {this.dropdActions[column].map((item, i) => (
                        <li key={i} className="c-dropd__list-item" onClick={item.action}>
                            {item.text}
                        </li>
                    ))}
                </ul>
            )
        );
    };

    sortableDropdHeader = (column, title) => {
        return (
            <div className="c-dropd">
                <div onClick={this.openDropd.bind(this, column)}>
                    {title}
                    <small className="ml-2">
                        <i className="icon-uEA68-sort-solid" />
                    </small>
                </div>
                {this.renderDropd(column)}
            </div>
        );
    };

    sortableHeader = (column, title) => {
        return (
            <div onClick={this.sortByColumn.bind(this, column)}>
                {title}
                <small className="ml-2">
                    <i className="icon-uEA68-sort-solid" />
                </small>
            </div>
        );
    };

    sortByColumn = (column) => {
        const { activeSort } = this.props;
        const direction = activeSort.column === column && activeSort.value === "asc" ? "desc" : "asc";
        this.props.sortData([{ name: column, value: direction }]);
    };

    headerClass = (headerName) => {
        const {
            activeSort: { column, value },
        } = this.props;
        const columnHeaderName = this.tableColumnMapper[column];
        return cn("b-table__header-cell", {
            "b-table__header-cell--asc": columnHeaderName === headerName && value === "asc",
            "b-table__header-cell--desc": columnHeaderName === headerName && value === "desc",
            "b-table__point-cell": ["from", "to", "car"].includes(headerName),
        });
    };

    toggleAutoRefresh = () => {
        clearInterval(this.refreshTimer);

        const newValue = !this.state.autoRefreshEnabled;
        this.setState({ autoRefreshEnabled: newValue });
        if (newValue) {
            this.createInterval();
        }
    };

    renderPagination = () => {
        const { count, params, isLoadingCount, withoutPageSize } = this.props;
        return params.length > 0 ? (
            <Pagination
                withoutPageSize={withoutPageSize}
                count={count}
                isNotExportable={true}
                pageSizeOptions={[30, 50, 100, 200]}
                isLoadingCount={isLoadingCount}
                length={params.length}
                start={params.start}
                pageSize={this.props.params.length}
                onPageSizeChange={this.changePageSize}
                action={this.changePage}
            >
                <div className="mr-auto">
                    <Tooltip
                        placement="top"
                        overlay={
                            <>
                                При включении опции список будет
                                <br />
                                автоматически обновляться раз в 5 минут
                            </>
                        }
                    >
                        <div
                            className={cn("pagination__refresh-btn", {
                                "pagination__refresh-btn--selected": this.state.autoRefreshEnabled,
                            })}
                            onClick={this.toggleAutoRefresh}
                        >
                            <span className="pagination__refresh-btn--checkbox" />
                            <span className="pagination__refresh-btn--text">Автообновление</span>
                        </div>
                    </Tooltip>
                </div>
            </Pagination>
        ) : null;
    };
}
